import { SpendSummarySearch } from '@/dashboards/SpendSummary';
import { FilterNode } from '@/dashboards/SpendSummary/SpendSummary';
import {
    useSpendSummaryData,
    useSpendSummaryUrl,
} from '@/dashboards/SpendSummary/hooks';
import { FormattedSpendSummaryData } from '@/dashboards/SpendSummary/types';
import {
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { createContext } from 'use-context-selector';

export const SpendSummaryContext = createContext<{
    spendSummaryData: FormattedSpendSummaryData;
    handleSelectedChange: Dispatch<SetStateAction<string>>;
    setSelectedNodes: (nodes: FilterNode[]) => void;
    modelBreakdown: SpendSummarySearch['modelBreakdown'];
    selectedFilterNodes: { [key: string]: FilterNode };
    getVisibleContributionData: () => {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    getVisibleSpendData: () => {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    selected: string;
    period: SpendSummarySearch['period'];
}>({
    spendSummaryData: {} as FormattedSpendSummaryData,
    handleSelectedChange: () => {},
    setSelectedNodes: () => {},
    modelBreakdown: 'category',
    selectedFilterNodes: {},
    getVisibleContributionData: () => ({}),
    getVisibleSpendData: () => ({}),
    selected: '',
    period: 'weekly',
});

export const SpendSummaryProvider = ({ children }: { children: ReactNode }) => {
    const [selected, setSelected] = useState<string>('');
    const [selectedFilterNodes, setSelectedFilterNodes] = useState<{
        [key: string]: FilterNode;
    }>({});
    const { search } = useSpendSummaryUrl();
    const spendSummaryData = useSpendSummaryData();

    const {
        contributionDataByVariable,
        variableToCategory,
        contributionDataByCategory,
        spendDataByCategory,
        spendDataByVariable,
    } = spendSummaryData;

    const firstRender = useRef(true);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        setSelected('');
    }, [search.modelBreakdown]);

    function setSelectedNodes(nodes: FilterNode[]) {
        const nodesMap = nodes.reduce((acc, node) => {
            acc[node.name] = node;
            return acc;
        }, {});

        if (!nodesMap[selected]) {
            setSelected('');
        }

        setSelectedFilterNodes(nodesMap);
    }

    const getVisibleContributionData = useCallback(() => {
        return Object.fromEntries(
            Array.from(
                search.modelBreakdown === 'category'
                    ? contributionDataByCategory
                    : contributionDataByVariable,
            ).filter(([name, _]) => {
                if (search.modelBreakdown === 'category')
                    return selectedFilterNodes[name];

                return (
                    selectedFilterNodes[name] ||
                    selectedFilterNodes[variableToCategory.get(name)!]
                );
            }),
        );
    }, [
        contributionDataByCategory,
        contributionDataByVariable,
        search.modelBreakdown,
        selectedFilterNodes,
        variableToCategory,
    ]);

    const getVisibleSpendData = useCallback(() => {
        return Object.fromEntries(
            Array.from(
                search.modelBreakdown === 'category'
                    ? spendDataByCategory
                    : spendDataByVariable,
            ).filter(([name, _]) => {
                if (search.modelBreakdown === 'category')
                    return selectedFilterNodes[name];

                return (
                    selectedFilterNodes[name] ||
                    selectedFilterNodes[variableToCategory.get(name)!]
                );
            }),
        );
    }, [
        search.modelBreakdown,
        selectedFilterNodes,
        spendDataByCategory,
        spendDataByVariable,
        variableToCategory,
    ]);

    return (
        <SpendSummaryContext.Provider
            value={{
                getVisibleContributionData,
                getVisibleSpendData,
                modelBreakdown: search.modelBreakdown,
                spendSummaryData,
                selected,
                handleSelectedChange: setSelected,
                setSelectedNodes,
                selectedFilterNodes,
                period: search.period,
            }}
        >
            {children}
        </SpendSummaryContext.Provider>
    );
};
